import React, { useState, useEffect, useRef } from 'react';
import { getAllUsers } from '../services/firebaseService';

const UserDropdown = ({ value = [], onChange, placeholder = "Select users", multiple = false, className = "" }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersData = await getAllUsers();
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredUsers = users.filter(user => 
    (user.displayName || user.email).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSelected = (user) => {
    const userValue = user.displayName || user.email;
    return Array.isArray(value) ? value.includes(userValue) : value === userValue;
  };

  const handleUserSelect = (user) => {
    const userValue = user.displayName || user.email;
    
    if (multiple) {
      const newValue = isSelected(user)
        ? value.filter(v => v !== userValue)
        : [...(value || []), userValue];
      onChange(newValue);
    } else {
      onChange(userValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  if (loading) {
    return (
      <div className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 ${className}`}>
        <span className="text-gray-500">Loading users...</span>
      </div>
    );
  }

  if (error) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
      />
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between min-h-[42px]"
      >
        <div className="flex flex-wrap gap-1 items-center">
          {multiple && value && value.length > 0 ? (
            value.map((val, index) => {
              const user = users.find(u => (u.displayName || u.email) === val);
              return user ? (
                <span key={index} className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full mr-1 mb-1">
                  <img
                    src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=e5e7eb&color=374151`}
                    alt={user.displayName || user.email}
                    className="w-4 h-4 rounded-full mr-1"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=e5e7eb&color=374151`;
                    }}
                  />
                  {user.displayName || user.email}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newValue = value.filter(v => v !== val);
                      onChange(newValue);
                    }}
                    className="ml-1 text-blue-400 hover:text-blue-600"
                  >
                    ×
                  </button>
                </span>
              ) : null;
            })
          ) : !multiple && value ? (
            <span className="inline-flex items-center">
              <img
                src={users.find(u => (u.displayName || u.email) === value)?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(value)}&background=e5e7eb&color=374151`}
                alt={value}
                className="w-5 h-5 rounded-full mr-2"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(value)}&background=e5e7eb&color=374151`;
                }}
              />
              {value}
            </span>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          <div className="p-2">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-48 overflow-auto">
            {filteredUsers.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">No users found</div>
            ) : (
              filteredUsers.map((user) => {
                const userValue = user.displayName || user.email;
                const selected = isSelected(user);
                
                return (
                  <li
                    key={user.uid}
                    onClick={() => handleUserSelect(user)}
                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center ${selected ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex items-center w-full">
                      <div className="flex-shrink-0">
                        <img
                          src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userValue)}&background=e5e7eb&color=374151`}
                          alt={userValue}
                          className="w-6 h-6 rounded-full mr-2"
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userValue)}&background=e5e7eb&color=374151`;
                          }}
                        />
                      </div>
                      <div className="flex-grow">
                        {user.displayName || user.email}
                      </div>
                      {multiple && (
                        <div className="flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {}}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      )}
                    </div>
                  </li>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
